<template>
  <div
    class="relative flex flex-col justify-end mt-1.5"
    :class="props.wrapperClass"
  >
    <label v-if="label" :for="id" class="font-bold p-0.5 py-1 text-gray-500">{{
      label
    }}</label>
    <div
      class="flex items-center rounded"
      :class="{
        'bg-white': !props.disabled,
        'bg-gray-50': props.disabled,
      }"
    >
      <input
        :id="id"
        ref="input"
        :autofocus="props.autofocus"
        :type="props.type"
        :disabled="props.disabled"
        :value="modelValue"
        class="border-2 hover:border-2 w-full rounded p-1.5 focus:border-black-500"
        :step="props.step"
        :pattern="props.pattern"
        :class="{
          [props.class]: true,
          '!p-[5px]': props.type == 'date',
          'border-red-600': props.error,
        }"
        :min="assignMinValue()"
        :max="assignMaxValue()"
        :required="props.required"
        :placeholder="props.placeholder"
        @click="
          e => {
            e.stopPropagation();
          }
        "
        @blur="e => (props.blur ? props.blur(e) : () => {})"
        @input="
          event => {
            let { value } = event.target;

            if (value < props.minNum && props.minNum != undefined) {
              input.value = '';
              emit('update:modelValue', value);
              return;
            } else if (value > props.maxNum && props.maxNum != undefined) {
              input.value = '';
              emit('update:modelValue', value);
              return;
            }

            if (props.step === '.01') {
              const parts = value.split('.');
              if (parts.length > 1) {
                value = parts[0] + '.' + parts[1].slice(0, 2); // Restrict to two decimal places
                input.value = value;
              }

              emit('update:modelValue', value);
            } else if (props.step === '1') {
              value = value.replace(/[^0-9]/g, ''); // Remove any non-numeric characters
              input.value = value;
              emit('update:modelValue', value);
            } else {
              emit('update:modelValue', value);
            }
          }
        "
      />
      <label v-show="props.suffix !== ''" class="ml-2">{{
        props.suffix
      }}</label>
    </div>
  </div>
</template>

<script setup>
import { format } from 'date-fns';
import { ref, nextTick, onMounted, onUpdated } from 'vue';

const input = ref();

const props = defineProps({
  label: {
    default: '',
    type: String,
  },
  modelValue: {
    default: '',
    type: String,
  },
  disabled: {
    default: false,
    type: Boolean,
  },
  class: {
    default: '',
    type: String,
  },
  type: {
    default: 'text',
    type: String,
  },
  minDate: {
    default: undefined,
    type: Date,
  },
  maxDate: {
    default: undefined,
    type: Date,
  },
  minNum: {
    default: null,
    type: Number,
  },
  maxNum: {
    default: null,
    type: Number,
  },
  suffix: {
    default: '',
    type: String,
  },
  autofocus: {
    default: false,
    type: Boolean,
  },
  required: {
    default: false,
    type: Boolean,
  },
  wrapperClass: {
    default: '',
    type: String,
  },
  placeholder: {
    default: '',
    type: String,
  },
  error: {
    default: false,
    type: Boolean,
  },
  step: {
    default: null,
    type: String,
  },
  pattern: {
    default: null,
    type: String,
  },
  id: {
    default: null,
    type: String,
  },
  blur: {
    default: null,
    type: Function,
  },
});

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      input.value && input.value.focus();
      if (props.modelValue.length) input.value && input.value.select();
    });
  }
});

onUpdated(() => {
  if (props.autofocus) {
    nextTick(() => {
      input.value && input.value.focus();
    });
  }
});

const id = props.id ? props.id : `base-input-${Date.now()}`;

function assignMinValue() {
  switch (props.type) {
    case 'float':
      return parseFloat(props.minNum);
    case 'number':
      return parseInt(props.minNum);
    case 'date':
      return props.minDate ? format(props.minDate, 'yyyy-MM-dd') : null;
    default:
      return null;
  }
}

function assignMaxValue() {
  switch (props.type) {
    case 'float':
      return parseFloat(props.minNum);
    case 'number':
      return parseInt(props.maxNum);
    case 'date':
      return props.maxDate ? format(props.maxDate, 'yyyy-MM-dd') : null;
    default:
      return null;
  }
}

const emit = defineEmits(['update:modelValue']);
</script>
